import { JSX, Suspense, useMemo } from "react";

import { Grid, Paper, Theme, useTheme } from "@mui/material";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useHistoryIemizedCompactChartQuery } from "src/api/locationChartQueries.api";
import CardHeader from "src/components/CardHeader/CardHeader";
import ChartSkeleton from "src/components/ChartSkeleton/ChartSkeleton";
import { ComponentParams } from "src/components/Dashboard/interfaces/DashboardComponentParams";
import DownloadCSV from "src/components/DownloadCSV/DownloadCSV";
import { IntervalTab } from "src/components/IntervalTabs/enum/IntervalTab";
import MetricHeader from "src/components/MetricHeader/MetricHeader";
import MetricLeftBlock from "src/components/MetricHeader/MetricLeftBlock";
import ComponentId from "src/constants/componentId.enum";
import IntervalType from "src/constants/intervalType.enum";
import sharedStyles from "src/constants/styles";
import { mapRegionFootfallPinnedSitesCompact } from "src/lib/chart.helper";
import { buildChartData, getDateTime, getIntervalFromSelectedTab, getMeasuresData, getToday } from "src/lib/date.helper";
import {
  filterByLocationTypeAndSortMeasuresItemizedCompactDto,
  formatNum,
  getCountDateTotalCount,
  getDifferenceForIntervalOverviewTabs,
} from "src/lib/number.helpers";
import useIntervalTabsStore from "src/store/intervalTabs.store";
import useAppPinnedStore from "src/store/pinned.store";
import { type MeasuresItemizedCompactDto, type MeasuresItemizedCompactItemDto } from "src/types/dashboardDatasetDto";
import LocationType from "src/types/LocationType";

import FootfallPinnedLocationsChart from "./FootFallPinnedLocationsChart";
import FootfallPinnedLocationsTable from "./FootfallPinnedLocationsTable";

const getStyles = (theme: Theme) => ({
  fullWidth: { xs: 12, md: 12 },

  chartContainer: {
    mt: 2,
    height: theme.spacing(36),
  },

  tableContainer: {
    mt: 2,
  },
});

function getHeaderTextFromDisplayedType(displayedType: LocationType): string {
  if (displayedType === LocationType.Mall) {
    return "footfall pinned malls";
  }
  if (displayedType === LocationType.Shop) {
    return "footfall pinned shops";
  }
  if (displayedType === LocationType.Station) {
    return "footfall pinned stations";
  }
  return "";
}

function FootfallPinnedLocations({ selectedLocation, displayedType, componentId }: ComponentParams): JSX.Element {
  const { appPinned } = useAppPinnedStore();
  const { t } = useTranslation();
  const theme = useTheme();
  const { componentStates } = useIntervalTabsStore();

  const componentState = componentStates[componentId] ?? {
    selectedTab: IntervalTab.Day,
    view: undefined,
    selectedDate: undefined,
  };

  const selectedDate = componentState.selectedDate ? dayjs(componentState.selectedDate) : null;

  const filteredAppPinned = useMemo(
    () => appPinned.filter((item) => item.type === displayedType).map((item) => item.id),
    [appPinned, displayedType]
  );

  const isPinned = useMemo(() => filteredAppPinned.length > 0, [filteredAppPinned]);

  const locationIds = useMemo(
    () => (isPinned ? filteredAppPinned : [selectedLocation.Id]),
    [isPinned, filteredAppPinned, selectedLocation.Id]
  );

  const { data: measuresDataToday } = useHistoryIemizedCompactChartQuery(locationIds, IntervalType.Day, selectedDate, null, isPinned);
  const { data: measuresDataWeek } = useHistoryIemizedCompactChartQuery(locationIds, IntervalType.Week, selectedDate, null, isPinned);
  const { data: measuresDataMonth } = useHistoryIemizedCompactChartQuery(locationIds, IntervalType.Month, selectedDate, null, isPinned);
  const { data: measuresDataYear } = useHistoryIemizedCompactChartQuery(locationIds, IntervalType.IsoYear, selectedDate, null, isPinned);

  const measuresData = useMemo(
    () => getMeasuresData(componentState.selectedTab, measuresDataToday, measuresDataWeek, measuresDataMonth, measuresDataYear),
    [componentState.selectedTab, measuresDataToday, measuresDataWeek, measuresDataMonth, measuresDataYear]
  ) as unknown as MeasuresItemizedCompactDto;

  let chartData: MeasuresItemizedCompactItemDto[] = [];

  if (measuresData) {
    chartData = buildChartData(measuresData, "compact", componentState.selectedTab) as MeasuresItemizedCompactItemDto[];
    measuresData.data = chartData;
  }

  const filteredMeasuresData: MeasuresItemizedCompactDto | undefined = useMemo(
    () => filterByLocationTypeAndSortMeasuresItemizedCompactDto(measuresData, displayedType),
    [measuresData, displayedType]
  );

  const diff = useMemo(() => getDifferenceForIntervalOverviewTabs(filteredMeasuresData), [filteredMeasuresData]);
  const totalCurrentValue = useMemo(
    () => (filteredMeasuresData?.data ? getCountDateTotalCount(filteredMeasuresData.data.flatMap((item) => item.currentValues ?? [])) : 0),
    [filteredMeasuresData]
  );

  const totalPastValue = useMemo(
    () => (filteredMeasuresData?.data ? getCountDateTotalCount(filteredMeasuresData.data.flatMap((item) => item.pastValues ?? [])) : 0),
    [filteredMeasuresData]
  );

  const intervalType = getIntervalFromSelectedTab(componentState.selectedTab);

  const dataRows = useMemo(
    () => (filteredMeasuresData ? mapRegionFootfallPinnedSitesCompact(filteredMeasuresData, intervalType, selectedLocation.LocalName) : []),
    [filteredMeasuresData, intervalType, selectedLocation.LocalName]
  );

  return (
    <Paper sx={sharedStyles(theme).cardContainer}>
      <CardHeader
        header={t(getHeaderTextFromDisplayedType(displayedType))}
        dateTime={getDateTime(filteredMeasuresData, componentState.selectedTab, selectedDate ?? getToday(), t)}
      />
      <MetricHeader
        leftContent={
          <MetricLeftBlock
            variant='footfall'
            totalCurrentValue={formatNum(totalCurrentValue)}
            totalPastValue={formatNum(totalPastValue)}
            diff={diff}
            pastLabel={t("previous year")}
          />
        }
        rightContent={
          <DownloadCSV componentId={ComponentId.FootfallPinnedLocations} locationName={selectedLocation.LocalName} rows={dataRows} />
        }
      />

      <Grid container spacing={2}>
        <Grid size={getStyles(theme).fullWidth} sx={getStyles(theme).chartContainer}>
          <Suspense fallback={<ChartSkeleton />}>
            {filteredMeasuresData?.data ? (
              <FootfallPinnedLocationsChart
                chartData={filteredMeasuresData?.data as unknown as MeasuresItemizedCompactItemDto[]}
                selectedTab={componentState.selectedTab}
              />
            ) : (
              <ChartSkeleton />
            )}
          </Suspense>
        </Grid>

        <Grid size={getStyles(theme).fullWidth} sx={getStyles(theme).tableContainer}>
          {filteredMeasuresData ? (
            <FootfallPinnedLocationsTable selectedLocation={selectedLocation} displayedType={displayedType} componentId={componentId} />
          ) : (
            <ChartSkeleton />
          )}
        </Grid>
      </Grid>
    </Paper>
  );
}

export default FootfallPinnedLocations;
